import type { ReactNode } from "react";

import { Button } from "../Button";
import { Dialog } from "../Dialog";

/**
 * 뭔가를 입력받아 확인하는 작은 창.
 *
 * 제목 · 설명 · 입력칸 · [취소][확인] — 이 뼈대가 이름 바꾸기와 비밀번호
 * 바꾸기에서 똑같이 반복돼서 하나로 모았다. (react-component-rules §4)
 *
 * 안쪽 내용은 `children`으로 뚫는다. 입력칸이 하나든 셋이든 뼈대는 같고,
 * 다른 것은 그 안뿐이기 때문이다. (§5-②)
 *
 * 이 컴포넌트는 메일을 모른다. 무엇을 입력받는지도 모른다.
 */

type FormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** 입력칸들 */
  children: ReactNode;
  /** 입력칸 아래 붙는 안내나 오류 한 줄 */
  hint?: ReactNode;
  submitLabel?: string;
  /**
   * 확인 단추의 생김새. 되돌릴 수 없는 것을 확인받을 때 `danger`.
   *
   * 색만 갈리고 뼈대는 같으므로 컴포넌트를 나누지 않는다
   * (react-component-rules §5-①).
   */
  submitVariant?: "primary" | "danger";
  pendingLabel?: string;
  cancelLabel?: string;
  pending?: boolean;
  canSubmit?: boolean;
  onSubmit: () => void;
};

export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  hint,
  submitLabel = "확인",
  submitVariant = "primary",
  pendingLabel = "처리 중…",
  cancelLabel = "취소",
  pending = false,
  canSubmit = true,
  onSubmit,
}: FormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="sm">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (pending || !canSubmit) return;
            onSubmit();
          }}
          className="flex flex-col gap-4 p-5"
        >
          <div className="flex flex-col gap-1">
            <Dialog.Title>{title}</Dialog.Title>
            {description ? (
              <Dialog.Description>{description}</Dialog.Description>
            ) : null}
          </div>

          {children}

          {hint}

          <div className="flex justify-end gap-2">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost">
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button
              type="submit"
              variant={submitVariant}
              disabled={pending || !canSubmit}
            >
              {pending ? pendingLabel : submitLabel}
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog>
  );
}
